import {
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { cn } from '../lib/cn.js';

/**
 * The editable counterpart to `DataGrid`.
 *
 * A voucher line grid is not a table of values, it is a table of controls, and
 * the two need different machinery: no sorting, no virtualisation, and cells
 * whose contents must not fight the density axis. The controls here are
 * deliberately chrome-less and sized as `calc(var(--row) - 1px)` so that
 * flipping `data-density` to compact still gives an accounts officer 31px rows
 * — a bordered `Input` at its own fixed height would burst them.
 *
 * `Field` + `Input` remain the right choice for the voucher *header*. This is
 * for the lines underneath it.
 */

export interface EntryColumn {
  key: string;
  label: ReactNode;
  /** Fixed width, e.g. '150px'. Omit on exactly the columns that should flex. */
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** Tabular figures and right alignment for the header label too. */
  numeric?: boolean;
  /** Renders the copper asterisk `Field` uses. */
  required?: boolean;
  /** Hidden when printing — row numbers are kept, delete buttons are not. */
  noPrint?: boolean;
}

const ALIGN = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
} as const;

export interface EntryGridProps {
  columns: EntryColumn[];
  /** One `EntryRow` per line. */
  children: ReactNode;
  /** Totals, rendered in a `tfoot` outside the row list. */
  footer?: ReactNode;
  className?: string;
  'aria-label': string;
}

export function EntryGrid({
  columns,
  children,
  footer,
  className,
  'aria-label': ariaLabel,
}: EntryGridProps) {
  return (
    <div className={cn('overflow-x-auto', className)}>
      <table className="w-full table-fixed border-collapse" aria-label={ariaLabel}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={c.width ? { width: c.width } : undefined} />
          ))}
        </colgroup>
        <thead>
          <tr className="bg-[var(--surface-2)]">
            {columns.map((c) => (
              <th
                key={c.key}
                scope="col"
                data-noprint={c.noPrint ? '1' : undefined}
                className={cn(
                  'border-b border-[var(--line-2)] px-2.5 py-2',
                  'text-[10px] font-medium tracking-[0.08em] whitespace-nowrap text-[var(--ink-3)] uppercase',
                  ALIGN[c.align ?? (c.numeric ? 'right' : 'left')],
                )}
              >
                {c.label}
                {c.required && (
                  <span className="pl-0.5 text-[var(--debit)]" aria-hidden="true">
                    *
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
        {footer && <tfoot className="bg-[var(--surface-2)] font-medium">{footer}</tfoot>}
      </table>
    </div>
  );
}

export interface EntryRowProps {
  children: ReactNode;
  /** Tints the whole row: a line that fails validation, a generated contra line. */
  tone?: 'default' | 'invalid' | 'generated';
  className?: string;
}

const ROW_TONE = {
  default: '',
  invalid: 'bg-[var(--debit-soft)]/45',
  generated: 'bg-[var(--surface-2)]',
} as const;

export function EntryRow({ children, tone = 'default', className }: EntryRowProps) {
  return (
    <tr
      className={cn(
        'bb-row-h border-b border-[var(--line-2)]',
        ROW_TONE[tone],
        tone === 'default' && 'focus-within:bg-[var(--accent-soft)]/25',
        className,
      )}
    >
      {children}
    </tr>
  );
}

export interface EntryCellProps {
  children?: ReactNode;
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;
  noPrint?: boolean;
  /** Adds the cell padding a control supplies for itself. Use for plain text. */
  padded?: boolean;
  className?: string;
}

export function EntryCell({
  children,
  align,
  numeric,
  noPrint,
  padded,
  className,
}: EntryCellProps) {
  return (
    <td
      data-noprint={noPrint ? '1' : undefined}
      className={cn(
        'p-0 align-middle text-[12.5px] text-[var(--ink)]',
        padded && 'px-2.5',
        ALIGN[align ?? (numeric ? 'right' : 'left')],
        numeric && 'bb-num tabular-nums',
        className,
      )}
    >
      {children}
    </td>
  );
}

/** Height and focus treatment shared by every control that lives in a cell. */
const CELL_CONTROL = cn(
  'h-[calc(var(--row)-1px)] w-full border-0 bg-transparent px-2.5 text-[12.5px]',
  'text-[var(--ink)] outline-none placeholder:text-[var(--ink-3)]',
  // An inset shadow rather than a ring or an outline: it cannot overflow the
  // cell, so a focused control never nudges the grid by a pixel.
  'focus:bg-[var(--accent-soft)]/60 focus:shadow-[inset_0_0_0_2px_var(--accent)]',
  'disabled:cursor-not-allowed disabled:text-[var(--ink-3)]',
  'aria-[invalid=true]:bg-[var(--debit-soft)] aria-[invalid=true]:text-[var(--debit)]',
);

/**
 * The chrome-less cell control, exported so `Combobox` can wear it.
 *
 * A searchable account picker has to look like the `GridSelect` it replaces —
 * same height, same focus ring, same behaviour under `data-density` — and the
 * only way for that to stay true through a design change is for both to read
 * the one definition.
 */
export { CELL_CONTROL as GRID_CELL_CONTROL };

export interface GridInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** Right-aligns and switches to tabular figures. Use for every amount cell. */
  numeric?: boolean;
}

export const GridInput = forwardRef<HTMLInputElement, GridInputProps>(function GridInput(
  { invalid, numeric, className, ...rest },
  ref,
) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      inputMode={numeric ? 'decimal' : undefined}
      className={cn(CELL_CONTROL, numeric && 'bb-num text-right tabular-nums', className)}
      {...rest}
    />
  );
});

export interface GridSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  invalid?: boolean;
}

export const GridSelect = forwardRef<HTMLSelectElement, GridSelectProps>(function GridSelect(
  { invalid, className, children, ...rest },
  ref,
) {
  return (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(CELL_CONTROL, 'cursor-pointer appearance-none pr-6', className)}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8' fill='none'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%238b9aa0' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 7px center',
      }}
      {...rest}
    >
      {children}
    </select>
  );
});

/**
 * Row-level control — the delete button at the end of a line. Sized to the cell
 * rather than to the 8px grid, so it stays centred at either density.
 */
export function GridAction({
  label,
  onClick,
  disabled,
  children,
  className,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'grid h-[calc(var(--row)-1px)] w-full cursor-pointer place-items-center border-0 bg-transparent',
        'text-[var(--ink-3)] hover:text-[var(--debit)]',
        'focus-visible:shadow-[inset_0_0_0_2px_var(--accent)] focus-visible:outline-none',
        'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-[var(--ink-3)]',
        className,
      )}
    >
      {children}
    </button>
  );
}
